import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { SEO } from "./seo/SEO";
import { SudokuBoard } from "./components/SudokuBoard";
import { StepsPanel } from "./components/StepsPanel";
import { useI18n } from "./i18n";
import { useAuth } from "./auth";
import { useRoutes } from "./hooks/useRoutes";
import { AppHeader } from "./components/AppHeader";
import type { Step, ChainData } from "@sudoku/shared";
import { track, createAttemptId } from "./lib/analytics";
import "./App.css";

export type { Step, ChainData };

type Board = (number | null)[][];

const API = import.meta.env.VITE_API_URL || "http://localhost:8000";

const emptyBoard = (): Board =>
  Array.from({ length: 9 }, () => Array(9).fill(null));

const MAX_OCR_IMAGE_DIMENSION = 2500;
const OCR_TIMEOUT_MS = 90_000;

/**
 * Telefon galerileri HEIC veya EXIF ile döndürülmüş, gereksiz büyük fotoğraflar
 * verebiliyor. Tarayıcının çözebildiği görselleri upload öncesinde standart
 * JPEG'e çevirerek backend'e yönü uygulanmış ve sınırlı boyutta veri gönderir.
 * Tarayıcı formatı çözemiyorsa orijinal dosyaya güvenli biçimde geri düşer.
 */
async function prepareImageForOcr(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, MAX_OCR_IMAGE_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      bitmap.close();
      return file;
    }
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92)
    );
    if (!blob) return file;

    const baseName = file.name.replace(/\.[^.]+$/, "") || "sudoku";
    return new File([blob], `${baseName}.jpg`, {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}

export default function App() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [board, setBoard] = useState<Board>(emptyBoard());
  const [originalBoard, setOriginalBoard] = useState<Board>(emptyBoard());
  const [inputBoard, setInputBoard] = useState<Board>(emptyBoard());
  const [uncertain, setUncertain] = useState<boolean[][]>(
    Array.from({ length: 9 }, () => Array(9).fill(false))
  );
  const [conflicts, setConflicts] = useState<boolean[][]>(
    Array.from({ length: 9 }, () => Array(9).fill(false))
  );
  const [status, setStatus] = useState<"idle" | "loading" | "solved" | "error" | "parsing" | "stepping">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [quotaTier, setQuotaTier] = useState<"anonymous" | "free" | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const { t } = useI18n();
  const { token, refreshAccessToken } = useAuth();
  const { r } = useRoutes();
  const [hoveredStepIndex, setHoveredStepIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const lastOcrAttemptIdRef = useRef<string | null>(null);
  const currentSolverAttemptIdRef = useRef<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [solvedBoard, setSolvedBoard] = useState<Board | null>(null);
  const [ocrSessionId, setOcrSessionId] = useState<string | null>(null);

  // Pre-populate board from ?board= query parameter (from game page solver link)
  useEffect(() => {
    const boardParam = searchParams.get('board');
    if (!boardParam) return;
    try {
      const decoded = decodeURIComponent(boardParam);
      const rows = decoded.split(';');
      if (rows.length !== 9) return;
      const parsed: Board = rows.map(row => {
        const cells = row.split(',').map(Number);
        if (cells.length !== 9) throw new Error('Invalid row');
        return cells.map(v => (v === 0 || isNaN(v) ? null : v));
      });
      setBoard(parsed);
      setOriginalBoard(parsed);
      setInputBoard(parsed);
      // Clean up the URL
      setSearchParams({}, { replace: true });
    } catch {
      // ignore invalid board param
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Adım adım modda board: inputBoard'dan başla, currentStepIndex'e kadar uygula
  const steppingBoard = useMemo(() => {
    if (status !== "stepping" || currentStepIndex < 0) return null;
    const b: Board = inputBoard.map(r => [...r]);
    for (let i = 0; i <= currentStepIndex; i++) {
      const s = steps[i];
      if (s && s.row >= 0 && s.col >= 0 && s.value > 0) {
        b[s.row][s.col] = s.value;
      }
    }
    return b;
  }, [status, currentStepIndex, steps, inputBoard]);

  const displayBoard = status === "stepping"
    ? (steppingBoard ?? inputBoard)
    : board;

  // Adım adım: mevcut adımın candidates snapshot'ını göster
  const displayCandidates = useMemo(() => {
    if (status !== "stepping") return null;
    if (currentStepIndex < 0) {
      return steps[0]?.candidates_snapshot ?? null;
    }
    if (currentStepIndex < steps.length - 1) {
      return steps[currentStepIndex + 1]?.candidates_snapshot ?? null;
    }
    return null;
  }, [status, currentStepIndex, steps]);

  // Vurgu hesaplama
  const { highlightTarget, highlightRelated, eliminatedCells, causeCells, linkCells, highlightedCandidates, placedCell, activeChainData } = useMemo(() => {
    const activeIndex = status === "stepping" ? currentStepIndex : hoveredStepIndex;
    if (activeIndex === null || activeIndex < 0) {
      return {
        highlightTarget: null,
        highlightRelated: new Set<string>(),
        eliminatedCells: new Set<string>(),
        causeCells: new Set<string>(),
        linkCells: new Set<string>(),
        highlightedCandidates: {} as Record<string, number[]>,
        placedCell: null as string | null,
        activeChainData: null as ChainData | null,
      };
    }
    const step = steps[activeIndex];
    if (!step) {
      return {
        highlightTarget: null,
        highlightRelated: new Set<string>(),
        eliminatedCells: new Set<string>(),
        causeCells: new Set<string>(),
        linkCells: new Set<string>(),
        highlightedCandidates: {} as Record<string, number[]>,
        placedCell: null as string | null,
        activeChainData: null as ChainData | null,
      };
    }

    const related: string[] = [];
    let target: string | null = null;
    let placed: string | null = null;
    const eliminated = new Set<string>();
    const cause = new Set<string>();
    const link = new Set<string>();
    const hlCands: Record<string, number[]> = {};

    if (step.row >= 0 && step.col >= 0) {
      target = `${step.row}-${step.col}`;
      if (status === "stepping") placed = target;
    }

    const ROW_MAP: Record<string, number> = { A:0, B:1, C:2, D:3, E:4, F:5, G:6, H:7, J:8 };

    // Parse cause_cells and elim_cells from step data
    const parseCellLabel = (label: string): string | null => {
      const m = label.match(/^([A-HJ])([1-9])$/);
      if (!m) return null;
      const r = ROW_MAP[m[1]];
      const c = parseInt(m[2]) - 1;
      if (r === undefined) return null;
      return `${r}-${c}`;
    };

    if (step.cause_cells && step.cause_cells.length > 0) {
      for (const label of step.cause_cells) {
        const key = parseCellLabel(label);
        if (key && key !== target) cause.add(key);
      }
    }

    if (step.elim_cells && step.elim_cells.length > 0) {
      for (const label of step.elim_cells) {
        const key = parseCellLabel(label);
        if (key) eliminated.add(key);
      }
    }

    // Fallback: parse cell refs from reason text if no structured data
    if (!step.cause_cells && !step.elim_cells) {
      const regex = /([A-HJ])([1-9])/g;
      let match;
      while ((match = regex.exec(step.reason)) !== null) {
        const r = ROW_MAP[match[1]];
        const c = parseInt(match[2]) - 1;
        if (r === undefined) continue;
        const key = `${r}-${c}`;
        if (key !== target && !related.includes(key)) related.push(key);
        if (step.value === 0 && step.row === -1) eliminated.add(key);
      }
    }

    if (step.link_cells && step.link_cells.length > 0) {
      for (const label of step.link_cells) {
        const key = parseCellLabel(label);
        if (key) link.add(key);
      }
    }

    if (step.highlighted_candidates) {
      for (const [label, nums] of Object.entries(step.highlighted_candidates)) {
        const key = parseCellLabel(label);
        if (key) hlCands[key] = nums;
      }
    }

    return {
      highlightTarget: target,
      highlightRelated: new Set(related),
      eliminatedCells: eliminated,
      causeCells: cause,
      linkCells: link,
      highlightedCandidates: hlCands,
      placedCell: placed,
      activeChainData: step.chain_data ?? null,
    };
  }, [hoveredStepIndex, currentStepIndex, steps, status]);

  function handleChange(row: number, col: number, value: number | null) {
    setBoard((prev) => {
      const next = prev.map((r) => [...r]);
      next[row][col] = value;
      return next;
    });
    setUncertain((prev) => {
      const next = prev.map((r) => [...r]);
      next[row][col] = false;
      return next;
    });
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const ocrAttemptId = createAttemptId();
    lastOcrAttemptIdRef.current = null; // Clear previous OCR until this one succeeds
    track("ocr_start", { source: "web_upload" }, { attempt_id: ocrAttemptId });
    setStatus("parsing");
    setErrorMsg("");
    setSteps([]);
    setCurrentStepIndex(-1);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), OCR_TIMEOUT_MS);
    try {
      const uploadFile = await prepareImageForOcr(file);
      const formData = new FormData();
      formData.append("file", uploadFile);
      let res = await fetch(`${API}/parse-image`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        method: "POST",
        body: formData,
        signal: controller.signal,
      });
      if (res.status === 401 && token) {
        const refreshed = await refreshAccessToken();
        res = await fetch(`${API}/parse-image`, {
          method: "POST", body: formData, signal: controller.signal,
          headers: refreshed ? { Authorization: `Bearer ${refreshed}` } : {},
        });
      }
      let data: { detail?: string; board?: unknown; uncertain?: unknown; conflicts?: unknown };
      try {
        data = await res.json();
      } catch {
        lastOcrAttemptIdRef.current = null;
        track("ocr_error", { source: "web_upload" }, { attempt_id: ocrAttemptId });
        setErrorMsg(t.solver.imageProcessError);
        setStatus("error");
        return;
      }
      if (!res.ok) {
        lastOcrAttemptIdRef.current = null;
        track("ocr_error", { source: "web_upload" }, { attempt_id: ocrAttemptId });
        setErrorMsg(data.detail || t.solver.imageProcessError);
        setStatus("error");
        return;
      }
      setBoard(data.board as Board);
      setUncertain(data.uncertain as boolean[][] || Array.from({ length: 9 }, () => Array(9).fill(false)));
      setConflicts(data.conflicts as boolean[][] || Array.from({ length: 9 }, () => Array(9).fill(false)));
      setOriginalBoard(emptyBoard());
      setOcrSessionId((data as { session_id?: string }).session_id ?? null);
      const filledCells = (data.board as Array<Array<number | null>>).reduce(
        (total, row) => total + row.filter(value => value != null && value !== 0).length,
        0,
      );
      track("ocr_success", { source: "web_upload", filled_cells: filledCells }, { attempt_id: ocrAttemptId });
      lastOcrAttemptIdRef.current = ocrAttemptId;
      setStatus("idle");
    } catch (err) {
      lastOcrAttemptIdRef.current = null;
      track("ocr_error", { source: "web_upload" }, { attempt_id: ocrAttemptId });
      if (err instanceof Error && err.name === "AbortError") {
        setErrorMsg(t.solver.imageTimeoutError);
      } else {
        setErrorMsg(t.solver.connectionError);
      }
      setStatus("error");
    } finally {
      clearTimeout(timeoutId);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  }

  function handleClear() {
    lastOcrAttemptIdRef.current = null;
    setBoard(emptyBoard());
    setOriginalBoard(emptyBoard());
    setInputBoard(emptyBoard());
    setUncertain(Array.from({ length: 9 }, () => Array(9).fill(false)));
    setConflicts(Array.from({ length: 9 }, () => Array(9).fill(false)));
    setStatus("idle");
    setErrorMsg("");
    setSteps([]);
    setCurrentStepIndex(-1);
    setSolvedBoard(null);
  }

  async function fetchSolution(): Promise<{ solved_board: Board; steps: Step[] } | null> {
    setErrorMsg("");
    setQuotaTier(null);
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;
      let res = await fetch(`${API}/solve`, {
        method: "POST",
        headers,
        body: JSON.stringify({ board }),
      });
      // Access token ~30dk'da expire olur — 401 alınca bir kez yenileyip tekrar
      // dener (mobile/app/game.tsx'teki fetchWithAuthRetry ile aynı desen).
      // Yenileme de başarısız olursa header'sız (anonim) devam eder.
      if (res.status === 401 && token) {
        const newToken = await refreshAccessToken();
        const retryHeaders: Record<string, string> = { "Content-Type": "application/json" };
        if (newToken) retryHeaders.Authorization = `Bearer ${newToken}`;
        res = await fetch(`${API}/solve`, {
          method: "POST",
          headers: retryHeaders,
          body: JSON.stringify({ board }),
        });
      }
      const data = await res.json();
      if (res.status === 429) {
        const tier = data.tier === "free" ? "free" : "anonymous";
        setQuotaTier(tier);
        setErrorMsg(tier === "free" ? t.solver.quotaExceededFree : t.solver.quotaExceededAnonymous);
        setStatus("error");
        return null;
      }
      if (!res.ok) {
        setErrorMsg(data.detail || t.solver.noSolution);
        setStatus("error");
        return null;
      }
      return data;
    } catch {
      setErrorMsg(t.solver.connectionError);
      setStatus("error");
      return null;
    }
  }

  async function handleSolve() {
    const solverAttemptId = createAttemptId();
    currentSolverAttemptIdRef.current = solverAttemptId;
    const startParams: Record<string, any> = { mode: "full" };
    if (lastOcrAttemptIdRef.current) startParams.ocr_attempt_id = lastOcrAttemptIdRef.current;
    track("solver_start", startParams, { attempt_id: solverAttemptId });
    setStatus("loading");
    setSteps([]);
    setCurrentStepIndex(-1);
    const boardSnapshot = board.map((r) => [...r]);
    // OCR ile gelmiş tahta onaylanıyor → eğitim verisi gönder (arka planda, hata yutulur)
    if (ocrSessionId) {
      fetch(`${API}/ocr/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: ocrSessionId, board: boardSnapshot }),
      }).catch(() => { /* sessizce yoksay */ });
      setOcrSessionId(null);
    }
    const data = await fetchSolution();
    if (!data) {
      track("solver_error", { mode: "full" }, { attempt_id: solverAttemptId });
      return;
    }
    setOriginalBoard(boardSnapshot);
    setInputBoard(boardSnapshot);
    setBoard(data.solved_board);
    setSolvedBoard(data.solved_board);
    setSteps(data.steps);
    track("solver_success", { mode: "full", steps: data.steps.length }, { attempt_id: solverAttemptId });
    lastOcrAttemptIdRef.current = null;
    setStatus("solved");
  }

  async function handleStepByStep() {
    const solverAttemptId = createAttemptId();
    currentSolverAttemptIdRef.current = solverAttemptId;
    const startParams: Record<string, any> = { mode: "step_by_step" };
    if (lastOcrAttemptIdRef.current) startParams.ocr_attempt_id = lastOcrAttemptIdRef.current;
    track("solver_start", startParams, { attempt_id: solverAttemptId });
    setStatus("loading");
    setSteps([]);
    setCurrentStepIndex(-1);
    const boardSnapshot = board.map((r) => [...r]);
    // OCR ile gelmiş tahta onaylanıyor → eğitim verisi gönder (arka planda)
    if (ocrSessionId) {
      fetch(`${API}/ocr/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: ocrSessionId, board: boardSnapshot }),
      }).catch(() => { /* sessizce yoksay */ });
      setOcrSessionId(null);
    }
    const data = await fetchSolution();
    if (!data) {
      track("solver_error", { mode: "step_by_step" }, { attempt_id: solverAttemptId });
      return;
    }
    setInputBoard(boardSnapshot);
    setOriginalBoard(boardSnapshot);
    setSolvedBoard(data.solved_board);
    setSteps(data.steps);
    track("solver_success", { mode: "step_by_step", steps: data.steps.length }, { attempt_id: solverAttemptId });
    lastOcrAttemptIdRef.current = null;
    setStatus("stepping");
  }

  const handleNextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) setCurrentStepIndex((i) => i + 1);
  }, [currentStepIndex, steps.length]);

  const handlePrevStep = useCallback(() => {
    if (currentStepIndex >= 0) setCurrentStepIndex((i) => i - 1);
  }, [currentStepIndex]);

  const handleGoToStep = useCallback((index: number) => {
    setCurrentStepIndex(Math.max(-1, Math.min(index, steps.length - 1)));
  }, [steps.length]);

  function handleShowAll() {
    if (solvedBoard) {
      setBoard(solvedBoard);
      setStatus("solved");
      setCurrentStepIndex(-1);
    }
  }

  function handleExportHTML() {
    if (steps.length === 0) return;

    // Build puzzle string from inputBoard
    const puzzleStr = inputBoard
      .map(row => row.map(c => c ?? 0).join(""))
      .join("");

    // Technique color map
    const techniqueColors: Record<string, string> = {
      "Naked Single": "#4ade80",
      "Hidden Single": "#60a5fa",
      "Naked Pair": "#f472b6",
      "Naked Triple": "#f472b6",
      "Naked Quad": "#f472b6",
      "Hidden Pair": "#c084fc",
      "Hidden Triple": "#c084fc",
      "Hidden Quad": "#c084fc",
      "Pointing Pairs": "#fb923c",
      "Box/Line Reduction": "#fb923c",
      "X-Wing": "#f87171",
      "Swordfish": "#f87171",
      "Jellyfish": "#f87171",
      "Finned X-Wing": "#f87171",
      "Finned Swordfish": "#f87171",
      "Skyscraper": "#fbbf24",
      "2-String Kite": "#fbbf24",
      "Empty Rectangle": "#fbbf24",
      "XY-Wing": "#a78bfa",
      "W-Wing": "#a78bfa",
      "XY-Chain": "#818cf8",
      "X-Cycles": "#818cf8",
      "Simple Colouring": "#2dd4bf",
      "Unique Rectangle": "#fb7185",
      "BUG+1": "#e879f9",
    };

    // Highlight cell references in reason text
    const highlightReason = (reason: string) => {
      return reason.replace(/([A-HJ])([1-9])/g, '<span class="cell-ref">$1$2</span>');
    };

    // Group steps by technique blocks
    const stepsHTML = steps.map((step, i) => {
      const color = techniqueColors[step.technique] || "#94a3b8";
      const reasonHTML = highlightReason(step.reason);
      return `<div class="step">
        <span class="step-num">${i + 1}.</span>
        <span class="technique" style="color: ${color}">[${step.technique}]</span>
        <span class="reason">${reasonHTML}</span>
      </div>`;
    }).join("\n");

    const html = `<!DOCTYPE html>
<html lang="tr">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Sudoku Solve Path</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    background: #1a1a2e;
    color: #e0e0e0;
    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    padding: 2rem;
    line-height: 1.6;
  }
  .container {
    max-width: 900px;
    margin: 0 auto;
  }
  h1 {
    color: #60a5fa;
    font-size: 1.6rem;
    margin-bottom: 0.5rem;
    border-bottom: 2px solid #334155;
    padding-bottom: 0.5rem;
  }
  .puzzle-info {
    background: #16213e;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 1rem 1.2rem;
    margin: 1rem 0 1.5rem;
    font-family: 'Cascadia Code', 'Fira Code', monospace;
    font-size: 0.9rem;
    color: #94a3b8;
    word-break: break-all;
  }
  .puzzle-info strong {
    color: #60a5fa;
  }
  .summary {
    background: #16213e;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 1rem 1.2rem;
    margin-bottom: 1.5rem;
  }
  .summary h2 {
    color: #60a5fa;
    font-size: 1.1rem;
    margin-bottom: 0.6rem;
  }
  .summary-item {
    display: inline-block;
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 4px;
    padding: 0.2rem 0.6rem;
    margin: 0.2rem 0.3rem;
    font-size: 0.85rem;
  }
  .steps-section h2 {
    color: #60a5fa;
    font-size: 1.1rem;
    margin-bottom: 0.8rem;
  }
  .step {
    padding: 0.5rem 0.8rem;
    border-left: 3px solid #334155;
    margin-bottom: 0.3rem;
    transition: background 0.15s;
  }
  .step:hover {
    background: #16213e;
  }
  .step-num {
    color: #64748b;
    font-size: 0.85rem;
    margin-right: 0.4rem;
    min-width: 2rem;
    display: inline-block;
  }
  .technique {
    font-weight: 700;
    margin-right: 0.5rem;
    font-size: 0.9rem;
  }
  .reason {
    color: #cbd5e1;
    font-size: 0.9rem;
  }
  .cell-ref {
    color: #fbbf24;
    font-weight: 600;
  }
  .footer {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #334155;
    color: #64748b;
    font-size: 0.8rem;
    text-align: center;
  }
</style>
</head>
<body>
<div class="container">
  <h1>Sudoku Solve Path</h1>
  <div class="puzzle-info">
    <strong>Puzzle:</strong> ${puzzleStr}
  </div>
  <div class="summary">
    <h2>Technique Summary (${steps.length} steps)</h2>
    ${(() => {
      const counts: Record<string, number> = {};
      steps.forEach(s => { counts[s.technique] = (counts[s.technique] || 0) + 1; });
      return Object.entries(counts)
        .map(([t, n]) => {
          const color = techniqueColors[t] || "#94a3b8";
          return `<span class="summary-item" style="border-color: ${color}"><span style="color: ${color}">${t}</span>: ${n}</span>`;
        })
        .join("\n    ");
    })()}
  </div>
  <div class="steps-section">
    <h2>Solution Steps</h2>
    ${stepsHTML}
  </div>
  <div class="footer">
    Generated by Sudoku Solver &mdash; ${new Date().toLocaleString("tr-TR")}
  </div>
</div>
</body>
</html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sudoku_solve_path_${Date.now()}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    if (status !== "stepping") return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); handleNextStep(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); handlePrevStep(); }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [status, handleNextStep, handlePrevStep]);

  const isLoading = status === "loading" || status === "parsing";

  return (
    <div className="app">
      <SEO path="/solver" />
      <AppHeader onBack={() => navigate(-1 as any)} backLabel={t.common.back} title={t.solver.title} />

      <div className="main-content">
        <div className="board-section">
          {status === "parsing" && (
            <div className="board-overlay">
              <div className="spinner" />
              <p>{t.solver.imageReading}</p>
            </div>
          )}
          <SudokuBoard
            board={displayBoard}
            originalBoard={originalBoard}
            uncertain={uncertain}
            conflicts={conflicts}
            onChange={handleChange}
            highlightTarget={highlightTarget}
            highlightRelated={highlightRelated}
            candidates={displayCandidates}
            eliminatedCells={eliminatedCells}
            causeCells={causeCells}
            linkCells={linkCells}
            highlightedCandidates={highlightedCandidates}
            placedCell={placedCell}
            chainData={activeChainData}
          />

          {status === "stepping" && (
            <div className="step-nav">
              <button className="btn btn-nav" onClick={handlePrevStep} disabled={currentStepIndex < 0} title={t.solver.prevTitle}>{t.solver.prev}</button>
              <span className="step-nav-info">
                {currentStepIndex < 0 ? t.solver.start : t.solver.stepInfo.replace('{current}', String(currentStepIndex + 1)).replace('{total}', String(steps.length))}
              </span>
              <button className="btn btn-nav" onClick={handleNextStep} disabled={currentStepIndex >= steps.length - 1} title={t.solver.nextTitle}>{t.solver.next}</button>
              <button className="btn btn-nav-end" onClick={handleShowAll} title={t.solver.showAllTitle}>{t.solver.showAll}</button>
            </div>
          )}

          <div className="controls">
            <button className="btn btn-upload" onClick={() => cameraInputRef.current?.click()} disabled={isLoading}>
              {status === "parsing" ? t.solver.reading : t.solver.takePhoto}
            </button>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleImageUpload} />
            <button className="btn btn-upload" onClick={() => fileInputRef.current?.click()} disabled={isLoading}>
              {status === "parsing" ? t.solver.reading : t.solver.chooseFromLibrary}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageUpload} />
            <button className="btn btn-primary" onClick={handleSolve} disabled={isLoading}>
              {status === "loading" ? t.solver.solving : t.solver.solve}
            </button>
            <button className="btn btn-step" onClick={handleStepByStep} disabled={isLoading}>
              {t.solver.stepByStep}
            </button>
            <button className="btn btn-secondary" onClick={handleClear}>{t.solver.clear}</button>
            {(status === "solved" || status === "stepping") && (
              <button className="btn btn-secondary" onClick={() => {
                setBoard(inputBoard.map(r => [...r]));
                setSteps([]);
                setCurrentStepIndex(-1);
                setSolvedBoard(null);
                setStatus("idle");
              }}>{t.game.undoSolve}</button>
            )}
            {steps.length > 0 && (
              <button className="btn btn-export" onClick={handleExportHTML} title={t.solver.exportTitle}>
                {t.solver.exportHtml}
              </button>
            )}
          </div>
          {status === "solved" && <p className="message success">{t.solver.solutionFound}</p>}
          {status === "error" && (
            <p className="message error">
              {errorMsg}
              {quotaTier === "anonymous" && (
                <> <Link to={r("home")}>{t.solver.quotaExceededLoginCta}</Link></>
              )}
              {quotaTier === "free" && (
                <> <Link to={r("premium")}>{t.solver.quotaExceededPremiumCta}</Link></>
              )}
            </p>
          )}
        </div>

        <StepsPanel
          steps={steps}
          onStepHover={status === "stepping" ? undefined : setHoveredStepIndex}
          activeStepIndex={status === "stepping" ? currentStepIndex : undefined}
          onStepClick={status === "stepping" ? handleGoToStep : undefined}
          mode={status === "stepping" ? "stepping" : "default"}
        />
      </div>
    </div>
  );
}
